import { logger } from '@mail-core/logger';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { checkGalleryPublished } from 'reactApp/api/vigbo/vigboGalleryApi';
import { IS_CHOSEN_PUBLIC_FILE } from 'reactApp/appHelpers/configHelpers';
import { showWatchVigboGallery } from 'reactApp/appHelpers/featuresHelpers/features/vigbo';
import { SelectionsSelectors } from 'reactApp/modules/selections/selections.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { getIdsCount, getStorageCurrentFolder } from 'reactApp/modules/storage/storage.selectors';
import { type EStorageType, EActions } from 'reactApp/modules/storage/storage.types';
import type { RootState } from 'reactApp/store';
import { TOOLBAR_CONFIG } from 'reactApp/ui/Toolbar/Toolbar.config';
import { toolbarItems } from 'reactApp/ui/Toolbar/Toolbar.data';
import { type TToolbarItem, ToolbarContextEnum } from 'reactApp/ui/Toolbar/Toolbar.types';

// eslint-disable-next-line complexity
export const useVigboRightActions = ({
    storage,
    toolbarType,
    setupVigboGalleryButtonRef,
}: {
    storage: EStorageType;
    toolbarType?: ToolbarContextEnum;
    setupVigboGalleryButtonRef?: React.RefObject<HTMLDivElement>;
}): TToolbarItem[] => {
    const vigboRightActions: TToolbarItem[] = [];
    const [isGalleryPublished, setIsGalleryPublished] = useState<boolean>(false);

    const settings = TOOLBAR_CONFIG[storage] || {};

    const { isVigbo } = getStorage(storage);
    const isSelectingMode = useSelector(SelectionsSelectors.isAnyIndexSelected);
    const currentFolder = useSelector((state: RootState) => getStorageCurrentFolder(state, storage));
    const itemsCount = useSelector((state: RootState) => getIdsCount(state, storage as EStorageType));

    useEffect(() => {
        if (!currentFolder?.id || !settings?.watchVigboGallery || !showWatchVigboGallery) {
            return;
        }

        checkGalleryPublished(currentFolder.id)
            .then((published) => {
                setIsGalleryPublished(published);
            })
            .catch((error) => {
                logger.error(error);
            });
    }, [currentFolder?.id, settings?.watchVigboGallery, setIsGalleryPublished]);

    if (!isVigbo) {
        return [];
    }

    if ((isSelectingMode || IS_CHOSEN_PUBLIC_FILE) && toolbarType !== ToolbarContextEnum.pageContentContext) {
        return [];
    }

    if (currentFolder) {
        if (settings?.watchVigboGallery && showWatchVigboGallery && isGalleryPublished) {
            // показываем кнопку только если галерея опубликована
            const watchVigboGalleryItem = toolbarItems[EActions.watchVigboGallery];
            watchVigboGalleryItem.disabled = itemsCount === 0;
            vigboRightActions.push(watchVigboGalleryItem);
        }

        if (settings?.setupVigboGallery) {
            const setupVigboGalleryItem = toolbarItems[EActions.setupVigboGallery];
            setupVigboGalleryItem.disabled = itemsCount === 0;
            vigboRightActions.push(setupVigboGalleryItem);
            if (itemsCount) {
                setupVigboGalleryItem.ref = setupVigboGalleryButtonRef;
            }
        }
    }

    return vigboRightActions;
};
